import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Trash2, SearchX } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SearchAnalyticsManager = () => {
  const [queries, setQueries] = useState([]);
  const [totalDistinct, setTotalDistinct] = useState(0);
  const [totalSearches, setTotalSearches] = useState(0);
  const [loading, setLoading] = useState(true);
  const [clearing, setClearing] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/search-analytics?limit=200`, {
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setQueries(data.queries || []);
        setTotalDistinct(data.total_distinct_queries || 0);
        setTotalSearches(data.total_failed_searches || 0);
      }
    } catch (err) {
      console.error('Failed to fetch search analytics:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleClear = async () => {
    if (!window.confirm('Изчисти целия лог с неуспешни търсения?')) return;
    setClearing(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/search-analytics`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (res.ok) {
        fetchData();
      }
    } catch (err) {
      console.error('Failed to clear search analytics:', err);
    } finally {
      setClearing(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    try {
      return new Date(iso).toLocaleString('bg-BG', {
        day: '2-digit', month: '2-digit', year: '2-digit',
        hour: '2-digit', minute: '2-digit',
      });
    } catch {
      return iso;
    }
  };

  return (
    <div className="search-analytics-manager" data-testid="search-analytics-manager">
      <div className="section-header">
        <h3>Търсения без резултат</h3>
        <button
          className="refresh-btn"
          onClick={fetchData}
          title="Обнови"
          data-testid="search-analytics-refresh-btn"
        >
          <RefreshCw size={16} />
        </button>
      </div>

      <p className="info-text">
        Заявки, които клиентите са търсили, но не са получили резултат. Използвай това за да откриеш
        липсващи продукти, грешки в изписването или марки, които клиентите очакват да намерят.
      </p>

      <div className="search-analytics-stats">
        <div className="stat-card">
          <span className="stat-label">Уникални заявки</span>
          <span className="stat-value">{totalDistinct}</span>
        </div>
        <div className="stat-card">
          <span className="stat-label">Общо търсения без резултат</span>
          <span className="stat-value highlight">{totalSearches}</span>
        </div>
      </div>

      {loading ? (
        <div className="search-analytics-loading">Зареждане...</div>
      ) : queries.length === 0 ? (
        <div className="search-analytics-empty">
          <SearchX size={28} />
          <span>Все още няма записани търсения без резултат.</span>
        </div>
      ) : (
        <div className="search-analytics-table-wrap">
          <table className="search-analytics-table" data-testid="search-analytics-table">
            <thead>
              <tr>
                <th>Заявка</th>
                <th>Брой пъти</th>
                <th>Последно търсено</th>
                <th>Източник</th>
              </tr>
            </thead>
            <tbody>
              {queries.map((row) => (
                <tr key={row.query} data-testid={`search-analytics-row-${row.query}`}>
                  <td className="query-cell">{row.query}</td>
                  <td>{row.count}</td>
                  <td>{formatDate(row.last_searched_at)}</td>
                  <td>{row.last_source === 'products_page' ? 'Страница с продукти' : 'Търсачка'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {queries.length > 0 && (
        <div className="search-analytics-actions">
          <button
            className="btn-secondary clear-log-btn"
            onClick={handleClear}
            disabled={clearing}
            data-testid="search-analytics-clear-btn"
          >
            <Trash2 size={16} />
            <span>{clearing ? 'Изчистване...' : 'Изчисти лог'}</span>
          </button>
        </div>
      )}

      <style>{`
        .search-analytics-manager {
          background: var(--bg-secondary);
          border: 1px solid var(--border-light);
          border-radius: 8px;
          padding: 24px;
          margin-top: 24px;
        }

        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }

        .section-header h3 {
          margin: 0;
          font-size: 18px;
          font-weight: 600;
        }

        .refresh-btn {
          background: none;
          border: 1px solid var(--border-light);
          padding: 8px;
          border-radius: 4px;
          cursor: pointer;
          color: var(--text-secondary);
          transition: all 0.2s;
        }

        .refresh-btn:hover {
          background: var(--bg-primary);
          color: var(--text-primary);
        }

        .info-text {
          color: var(--text-secondary);
          font-size: 14px;
          line-height: 1.5;
          margin: 0 0 20px;
        }

        .search-analytics-stats {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 16px;
          margin-bottom: 24px;
        }

        .stat-card {
          background: var(--bg-primary);
          padding: 16px;
          border-radius: 6px;
          text-align: center;
        }

        .stat-label {
          display: block;
          font-size: 12px;
          color: var(--text-secondary);
          margin-bottom: 4px;
        }

        .stat-value {
          display: block;
          font-size: 24px;
          font-weight: 600;
          color: var(--text-primary);
        }

        .stat-value.highlight {
          color: var(--accent-gold, #c9a86c);
        }

        .search-analytics-loading,
        .search-analytics-empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 10px;
          padding: 40px 0;
          color: var(--text-secondary);
          font-size: 14px;
        }

        .search-analytics-table-wrap {
          overflow-x: auto;
          border: 1px solid var(--border-light);
          border-radius: 6px;
        }

        .search-analytics-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 14px;
        }

        .search-analytics-table th,
        .search-analytics-table td {
          text-align: left;
          padding: 12px 14px;
          border-bottom: 1px solid var(--border-light);
          white-space: nowrap;
        }

        .search-analytics-table th {
          background: var(--bg-primary);
          color: var(--text-secondary);
          font-size: 12px;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .search-analytics-table tbody tr:last-child td {
          border-bottom: none;
        }

        .query-cell {
          font-weight: 500;
          color: var(--text-primary);
        }

        .search-analytics-actions {
          display: flex;
          justify-content: flex-end;
          margin-top: 16px;
        }

        .clear-log-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 18px;
          font-size: 13px;
          border-radius: 6px;
          cursor: pointer;
          background: none;
          border: 1px solid var(--border-light);
          color: var(--text-primary);
          transition: all 0.2s;
        }

        .clear-log-btn:hover:not(:disabled) {
          background: rgba(239, 68, 68, 0.08);
          border-color: rgba(239, 68, 68, 0.4);
          color: #ef4444;
        }

        .clear-log-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        @media (max-width: 640px) {
          .search-analytics-stats {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default SearchAnalyticsManager;
